import { useCallback, useEffect, useMemo, useRef, useState } from "react";


const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const BACKEND_TOKEN = process.env.REACT_APP_BACKEND_TOKEN;
const BACKEND_WS = process.env.REACT_APP_BACKEND_WS_URL;

const WS_LOC = `${BACKEND_WS}ws/live/vehicles/location`;
const WS_AL = `${BACKEND_WS}ws/live/vehicles/alerts/count`;
const WS_ST = `${BACKEND_WS}ws/live/vehicles/status`;

const pickVehicleId = (vehicle, fallback) => {
	if (!vehicle || typeof vehicle !== "object") return fallback ?? null;
	const id =
		vehicle.veh_id ??
		vehicle.vehicle_id ??
		vehicle.id ??
		vehicle.device_vehicle_id ??
		vehicle.pk ??
		fallback ??
		null;
	if (id == null) return null;
	return id;
};

const normalizeVehiclesPayload = (payload) => {
	const map = {};
	const order = [];

	const assign = (vehicle, hintedKey) => {
		if (!vehicle) return;
		const rawId = pickVehicleId(vehicle, hintedKey);
		if (rawId == null || rawId === "") return;
		const id = String(rawId);
		if (!map[id]) order.push(id);
		map[id] =
			vehicle.veh_id != null && vehicle.veh_id !== ""
				? vehicle
				: { ...vehicle, veh_id: rawId };
	};

	if (Array.isArray(payload)) {
		payload.forEach((vehicle) => assign(vehicle));
	} else if (payload && typeof payload === "object") {
		if (Array.isArray(payload.results)) {
			payload.results.forEach((vehicle) => assign(vehicle));
		} else {
			Object.entries(payload).forEach(([key, vehicle]) => assign(vehicle, key));
		}
	}

	return {
		map,
		order: order.length ? order : Object.keys(map),
	};
};

export default function useFleet() {
	const vehiclesRef = useRef({});
	const [orderedIds, setOrderedIds] = useState([]);
	const [counts, setCounts] = useState({
		ALL: 0,
		IDLE: 0,
		MOVING: 0,
		CHARGING: 0,
		OFFLINE: 0,
	});

	const loadingRef = useRef(false);

	const fetchVehicles = useCallback(async () => {
		if (loadingRef.current) return;
		loadingRef.current = true;
		try {
			const res = await fetch(`${BACKEND_URL}vehicles/`, {
				headers: { Authorization: `Bearer ${BACKEND_TOKEN}` },
			});
			const data = await res.json();
			if (!data?.success)
				throw new Error(data?.message || "Bad response");

			const payload = data.data ?? data;
			const { map, order } = normalizeVehiclesPayload(payload || {});
			vehiclesRef.current = map;
			setOrderedIds(order);

			const values = order.map((id) => map[id]).filter(Boolean);
			const baseCounts = { IDLE: 0, MOVING: 0, CHARGING: 0, OFFLINE: 0 };
			for (const vehicle of values) {
				const status = vehicle?.st ?? "OFFLINE";
				if (baseCounts[status] == null) baseCounts[status] = 0;
				baseCounts[status] += 1;
			}

			setCounts({
				ALL: values.length,
				IDLE: baseCounts.IDLE ?? 0,
				MOVING: baseCounts.MOVING ?? 0,
				CHARGING: baseCounts.CHARGING ?? 0,
				OFFLINE: baseCounts.OFFLINE ?? 0,
			});
		} catch (err) {
			console.error("Vehicle list fetch error:", err);
		} finally {
			loadingRef.current = false;
		}
	}, []);

	useEffect(() => {
		fetchVehicles();
	}, [fetchVehicles]);

	const safeUpdate = (veh_id, updater) => {
		const id = String(veh_id);
		const old = vehiclesRef.current[id];
		if (!old) return;
		const updated = updater(old);
		if (!updated || Object.is(old, updated)) return;
		vehiclesRef.current[id] = updated;
	};

	useEffect(() => {
		const ws = new WebSocket(WS_LOC);
		ws.onmessage = (e) => {
			try {
				const { veh_id, loc } = JSON.parse(e.data);
				if (!veh_id || !loc) return;
				safeUpdate(veh_id, (old) => {
					if (
						old.loc?.lat === loc.lat &&
						old.loc?.lng === loc.lng &&
						old.loc?.alt === loc.alt
					)
						return old;
					return { ...old, loc };
				});
			} catch {}
		};
		return () => ws.close();
	}, []);

	useEffect(() => {
		const ws = new WebSocket(WS_AL);
		ws.onmessage = (e) => {
			try {
				const { veh_id, al } = JSON.parse(e.data);
				if (veh_id == null) return;
				safeUpdate(veh_id, (old) =>
					old.al === al ? old : { ...old, al }
				);
			} catch {}
		};
		return () => ws.close();
	}, []);

	useEffect(() => {
		const ws = new WebSocket(WS_ST);
		ws.onmessage = (e) => {
			try {
				const { veh_id, st, cnts } = JSON.parse(e.data);
				if (veh_id && st)
					safeUpdate(veh_id, (old) =>
						old.st === st ? old : { ...old, st, status: st }
					);
				if (cnts)
					setCounts((prev) => ({
						...prev,
						...cnts,
						ALL: orderedIds.length || prev.ALL,
					}));
			} catch {}
		};
		return () => ws.close();
	}, [orderedIds.length]);

	const vehiclesMap = vehiclesRef.current;
	const list = useMemo(
		() => orderedIds.map((id) => vehiclesMap[id]).filter(Boolean),
		[orderedIds, vehiclesMap]
	);

	return {
		vehiclesMap,
		orderedIds,
		list,
		counts,
		refetchVehicles: fetchVehicles,
	};
}
